import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  DirectionsRun as RunIcon,
  DirectionsBike as BikeIcon,
  Pool as SwimIcon,
  FitnessCenter as OtherIcon,
  OpenInNew as ExternalLinkIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { AthleteDayGroup, formatDuration, formatPace, formatStartTime } from './athleteActivityStats';
import { AthleteActivityIntegrity, ReviewSuspiciousHandler } from './AthleteActivityIntegrity';

const iconFor = (workoutType: string) => {
  switch ((workoutType || '').toLowerCase()) {
    case 'ride':
    case 'bike':
      return <BikeIcon fontSize="inherit" />;
    case 'swim':
      return <SwimIcon fontSize="inherit" />;
    case 'run':
      return <RunIcon fontSize="inherit" />;
    default:
      return <OtherIcon fontSize="inherit" />;
  }
};

const tabular = { fontVariantNumeric: 'tabular-nums' } as const;

// One bordered day: muted header with day totals, then one row per activity.
export const AthleteActivityDayCard = ({ day, onReviewSuspicious }: { day: AthleteDayGroup; onReviewSuspicious?: ReviewSuspiciousHandler }) => {
  const { t } = useTranslation('contests');
  return (
    <Box sx={{ border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px 12px', alignItems: 'center', p: '10px 14px', backgroundColor: 'var(--muted-bg)', fontSize: 13 }}>
        <Box component="span" sx={{ fontWeight: 600, flex: 1, minWidth: 110 }}>{day.label}</Box>
        <Box component="span" sx={{ color: 'var(--muted)' }}>
          {t('athlete.activityCount', { count: day.activities.length })}
        </Box>
        <Box component="span" sx={{ color: 'var(--muted)', ...tabular }}>{formatDuration(day.totalTime)}</Box>
        <Box component="span" sx={{ fontWeight: 600, ...tabular }}>{t('common:units.km', { value: day.totalDistance.toFixed(1) })}</Box>
      </Box>
      {day.activities.map(a => (
        <Box key={a.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: '12px 14px', borderTop: '1px solid var(--border)' }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Box sx={{ width: 34, height: 34, flex: 'none', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, backgroundColor: 'var(--accent-bg)', color: 'var(--accent-fg)' }}>
              {iconFor(a.workoutType)}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <Box component="span" sx={{ fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {a.workoutType || t('athlete.activityFallback')}
                {a.suspicious && !a.suspiciousDismissed && (
                  <WarningIcon titleAccess={t('athlete.integrity.flagged')} sx={{ fontSize: 16, color: 'var(--warn)' }} />
                )}
              </Box>
              <Box component="span" sx={{ fontSize: 12, color: 'var(--muted)', display: 'flex', flexWrap: 'wrap', gap: '4px 10px', ...tabular }}>
                <span>{formatStartTime(a.startDate)}</span>
                <span>{formatDuration(a.movingTime)}</span>
                <span>{formatPace(a.pace)} {t('common:units.perKm')}</span>
              </Box>
            </Box>
            <Box component="span" sx={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', ...tabular }}>{t('common:units.km', { value: a.distance.toFixed(1) })}</Box>
            {a.stravaActivityId ? (
              <Box
                component="a"
                href={`https://www.strava.com/activities/${a.stravaActivityId}`}
                target="_blank"
                rel="noopener noreferrer"
                title={t('athlete.openInStrava')}
                aria-label={t('athlete.openInStrava')}
                sx={{ color: 'var(--muted)', display: 'flex', fontSize: 16, '&:hover': { color: 'var(--fg)' } }}
              >
                <ExternalLinkIcon fontSize="inherit" />
              </Box>
            ) : null}
          </Box>
          <AthleteActivityIntegrity activity={a} onReview={onReviewSuspicious} />
        </Box>
      ))}
    </Box>
  );
};
